import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { IconButton } from 'shared/buttons'

const entriesOf = (rows) => rows.map((row) => row.trim()).filter(Boolean)
const key = (list) => JSON.stringify(list)

// A list-valued config setting edited one entry per row, for entries long
// enough to wrap or containing commas (addresses), where a text box can't
// show where one entry ends. Blank rows are kept while editing but never saved.
const ListRowsConfigInput = ({ value, onChange, itemLabel = 'entry', hint, disabled = false }) => {
  const list = Array.isArray(value) ? value : []
  const [rows, setRows] = useState(list)
  const lastEmitted = useRef(key(list))
  const inputs = useRef([])
  const [focusIndex, setFocusIndex] = useState(null)

  // Follow changes made outside this input, e.g. a reset to saved values.
  useEffect(() => {
    if (key(list) !== lastEmitted.current) {
      lastEmitted.current = key(list)
      setRows(list)
    }
  }, [list])

  useEffect(() => {
    if (focusIndex === null) return
    inputs.current[focusIndex]?.focus()
    setFocusIndex(null)
  }, [focusIndex])

  const update = (next) => {
    setRows(next)
    const entries = entriesOf(next)
    lastEmitted.current = key(entries)
    onChange(entries)
  }

  const addRow = (at = rows.length) => {
    update([...rows.slice(0, at), '', ...rows.slice(at)])
    setFocusIndex(at)
  }

  return (
    <div data-testid="list-rows-config">
      {rows.map((row, index) => (
        <div key={index} className="d-flex align-items-center gap-2 mb-2">
          <Form.Control
            size="sm"
            value={row}
            title={row}
            aria-label={`${itemLabel} ${index + 1}`}
            ref={(el) => {
              inputs.current[index] = el
            }}
            onChange={(e) => update(rows.map((r, i) => (i === index ? e.target.value : r)))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addRow(index + 1)
              }
            }}
            disabled={disabled}
          />
          <IconButton
            icon={faTimes}
            title={`Remove ${itemLabel}`}
            data-testid="list-rows-remove"
            onClick={() => update(rows.filter((_, i) => i !== index))}
            disabled={disabled}
          />
        </div>
      ))}
      <Button variant="light" className="text-primary" size="sm" onClick={() => addRow()} disabled={disabled}>
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        Add {itemLabel}
      </Button>
      {hint && (
        <Form.Text className="text-muted d-block" data-testid="list-config-hint">
          {hint}
        </Form.Text>
      )}
    </div>
  )
}

ListRowsConfigInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  itemLabel: PropTypes.string,
  hint: PropTypes.string,
  disabled: PropTypes.bool
}

export default ListRowsConfigInput
